import React, { useState } from 'react'
import styled from '@emotion/styled'
import { NavLink } from 'react-router-dom'

import LogoutIcon from '@mui/icons-material/Logout'
import HomeIcon from '@mui/icons-material/Home'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import CheckIcon from '@mui/icons-material/Check'
import AssessmentIcon from '@mui/icons-material/Assessment'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import SettingsIcon from '@mui/icons-material/Settings'

import { config } from '../../config'
import pirTerminology from '../../util/pirTerminology'
import { theme } from '../../themes/theme'
import { logo, verticalLogo } from '../../images/images.js'
import { FlexColumn, PrimaryColorDivider } from '../styles/Containers.js'
import { BoldBodyText, MedBodyText } from '../styles/Text.js'
import PoweredBy from './PoweredBy.js'

const SidebarContainer = styled(FlexColumn)`
  min-height: calc(100vh - 100px);
  width: max-content;

  position: sticky;
  top: 0;
  align-self: flex-start;
  justify-content: space-between;
  align-items: center;

  background: ${theme.colors.primaryExtraLight};
  padding: ${props => (props.open ? '50px 30px' : '50px 10px')};
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
`

const Logo = styled.img`
  height: ${props => (props.open ? '100px' : '80px')};
  width: ${props => (props.open ? '140px' : '80px')};
  padding: ${props => (props.open ? '0' : '10px 0')};
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  object-fit: contain;
`

const SectionHeader = styled(BoldBodyText)`
  color: ${theme.colors.primary};
  text-align: ${props => (props.open ? 'left' : 'center')};
  max-width: ${props => (props.open ? '150px' : '52px')};

  text-overflow: ${props => !props.open && 'ellipsis'};
  white-space: ${props => !props.open && 'nowrap'};
  overflow: ${props => !props.open && 'hidden'};
`

const NavWrapper = styled.div`
  border-radius: ${theme.borderRadius.button};

  a.active {
    background: ${theme.colors.secondaryButton.background};
    :hover {
      background: ${theme.colors.primaryLight};
    }
  }

  :hover {
    background: ${theme.colors.primaryLight};
  }
`

const NavItem = styled(NavLink)`
  display: flex;
  text-transform: none;
  align-items: center;
  gap: 5px;

  border-radius: ${theme.borderRadius.button};
  padding: 10px 15px;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;

  color: ${theme.colors.text.body};
  text-decoration: none;

  p {
    color: ${theme.colors.text.body};
  }

  .MuiSvgIcon-root {
    height: 22px;
    width: 22px;
  }
`

const CollapseButton = styled.div`
  position: absolute;
  top: 220px;
  right: 0;

  display: flex;
  align-items: center;

  background: ${theme.colors.secondaryButton.background};
  border-radius: 50px 0 0 50px;
  padding: ${props => (props.open ? '14px 0 14px 11px' : '14px 2px 14px 9px')};
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;

  .MuiSvgIcon-root {
    height: 22px;
  }

  :hover {
    cursor: pointer;
    background: ${theme.colors.primaryLight};
  }
`

function Sidebar() {
  const [isOpen, setIsOpen] = useState(true)

  const handleOpenChange = () => setIsOpen(isOpen => !isOpen)

  return (
    <SidebarContainer open={isOpen}>
      <Logo src={isOpen ? logo : verticalLogo} open={isOpen} />
      <CollapseButton onClick={handleOpenChange} aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'} open={isOpen}>
        {isOpen ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
      </CollapseButton>

      <FlexColumn gap="35px" width={isOpen ? '100%' : undefined}>
        {config.foi.active && (
          <FlexColumn gap="5px">
            <SectionHeader open={isOpen}>FOIs</SectionHeader>
            <PrimaryColorDivider padding="0 0 5px 0" />
            <NavWrapper>
              <NavItem to="/foi/tasks" open={isOpen} aria-label="Tasks">
                <TaskAltIcon />
                {isOpen && <MedBodyText>Tasks</MedBodyText>}
              </NavItem>
            </NavWrapper>
            <NavWrapper>
              <NavItem to="/foi/inprogress" open={isOpen} aria-label="In Progress FOIs">
                <TimelapseIcon />
                {isOpen && <MedBodyText>In Progress</MedBodyText>}
              </NavItem>
            </NavWrapper>
            <NavWrapper>
              <NavItem to="/foi/completed" open={isOpen} aria-label="Completed FOIs">
                <CheckIcon />
                {isOpen && <MedBodyText>Completed</MedBodyText>}
              </NavItem>
            </NavWrapper>
            <NavWrapper>
              <NavItem to="/foi/create" open={isOpen} aria-label="Create FOI">
                <NoteAddIcon />
                {isOpen && <MedBodyText>Create</MedBodyText>}
              </NavItem>
            </NavWrapper>
            <NavWrapper>
              <NavItem to="/foi/reports" open={isOpen} aria-label="FOI Reports">
                <AssessmentIcon />
                {isOpen && <MedBodyText>Reports</MedBodyText>}
              </NavItem>
            </NavWrapper>
          </FlexColumn>
        )}
        {config.pir.active && (
          <FlexColumn gap="5px" width={isOpen ? '100%' : undefined}>
            <SectionHeader open={isOpen} title={isOpen ? undefined : pirTerminology.getRequestPluralName()}>
              {pirTerminology.getRequestPluralName()}
            </SectionHeader>
            <PrimaryColorDivider padding="0 0 5px 0" />
            <NavWrapper>
              <NavItem to="/pir/dashboard" open={isOpen} aria-label={`${pirTerminology.getRequestShortName()} Dashboard`}>
                <HomeIcon />
                {isOpen && <MedBodyText>Dashboard</MedBodyText>}
              </NavItem>
            </NavWrapper>
          </FlexColumn>
        )}
      </FlexColumn>
      <FlexColumn gap="30px" width={isOpen ? '100%' : undefined}>
        <FlexColumn gap="5px">
          {config.foi.active && (
            <NavWrapper>
              <NavItem to="/settings" open={isOpen} aria-label="Settings">
                <SettingsIcon />
                {isOpen && <MedBodyText>Settings</MedBodyText>}
              </NavItem>
            </NavWrapper>
          )}
          <NavWrapper>
            <NavItem to="/logout" open={isOpen} aria-label="Log Out">
              <LogoutIcon />
              {isOpen && <MedBodyText>Log Out</MedBodyText>}
            </NavItem>
          </NavWrapper>
        </FlexColumn>
        <PoweredBy type={isOpen ? 'vertical' : 'paddedLogo'} />
      </FlexColumn>
    </SidebarContainer>
  )
}

export default Sidebar
